/**
 * InfoToast - modal dialog showing definition.info on project load.
 * Re-shown when user clicks the project name in the header.
 * Dismissed by clicking OK or clicking outside the box.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useScope } from '../contexts/ScopeContext';
import { useAppState } from '../contexts/AppContext';

export function InfoToast() {
  const { definitionState, definitionId } = useScope();
  const { projectName } = useAppState();
  const info = definitionState?.info;
  const title = definitionId === 'root' ? projectName : definitionId;

  const [visible, setVisible] = useState(false);
  const shownRef = useRef<string | undefined>(undefined);
  const boxRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    if (!info) return;
    setVisible(true);
  }, [info]);

  const dismiss = useCallback(() => setVisible(false), []);

  // Show on load when info changes (new project)
  useEffect(() => {
    if (!info || info === shownRef.current) return;
    shownRef.current = info;
    show();
  }, [info, show]);

  // Listen for header click event
  useEffect(() => {
    const handler = () => show();
    window.addEventListener('show-info-toast', handler);
    return () => window.removeEventListener('show-info-toast', handler);
  }, [show]);

  if (!visible || !info) return null;

  return (
    // Backdrop — click outside dismisses
    <div onClick={dismiss} style={styles.backdrop}>
      {/* Dialog box — stop propagation so clicking inside doesn't dismiss */}
      <div ref={boxRef} onClick={e => e.stopPropagation()} style={styles.dialog}>
        {title && <div style={styles.title}>{title}</div>}
        <div style={styles.text}>{info}</div>
        <button onClick={dismiss} style={styles.ok}>OK</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  dialog: {
    background: '#1e293b',
    color: '#cbd5e1',
    padding: '24px 28px',
    borderRadius: 10,
    maxWidth: 520,
    lineHeight: 1.6,
    fontSize: 14,
    border: '1px solid rgba(148, 163, 184, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: '#e2e8f0',
  },
  text: {
    whiteSpace: 'pre-wrap',
  },
  ok: {
    alignSelf: 'flex-end',
    background: '#374151',
    color: 'white',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '5px 20px',
    fontSize: 13,
    cursor: 'pointer',
  },
};
